"use client";

import * as React from "react";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";

import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

interface ReusableDatePickerProps {
  value?: Date;
  defaultValue?: Date;
  onDateChange?: (date: Date | undefined) => void;
  placeholder?: string;
  popupLabel?: string;
  disabled?: boolean;
  fromYear?: number;
  toYear?: number;
  className?: string;
  buttonClassName?: string;
}

export function ReusableDatePicker({
  value,
  defaultValue,
  onDateChange,
  placeholder = "Select date",
  popupLabel = "Select date",
  disabled = false,
  fromYear = 1950,
  toYear = new Date().getFullYear() + 20,
  className,
  buttonClassName,
}: ReusableDatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [internalDate, setInternalDate] = React.useState<Date | undefined>(
    defaultValue,
  );

  const selectedDate = value ?? internalDate;

  const handleSelect = (date: Date | undefined) => {
    if (value === undefined) {
      setInternalDate(date);
    }
    onDateChange?.(date);
    setOpen(false);
  };

  return (
    <div className={cn("w-full", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            className={cn(
              "w-full justify-between font-normal",
              !selectedDate && "text-muted-foreground",
              buttonClassName,
            )}
          >
            {selectedDate ? format(selectedDate, "dd/MM/yyyy") : placeholder}
            <CalendarIcon className="ml-2 h-4 w-4 shrink-0 opacity-60" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <div className="px-3 pt-3 text-xs font-semibold text-brand-textBlack">
            {popupLabel}
          </div>
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={handleSelect}
            captionLayout="dropdown"
            fromYear={fromYear}
            toYear={toYear}
            classNames={{
              caption_label: "sr-only",
              caption_dropdowns: "flex items-center gap-2 text-sm",
              dropdown:
                "h-8 rounded-md border border-input bg-background px-2 min-w-[70px] text-xs py-0",
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default ReusableDatePicker;
